import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  PlusIcon, PencilSimpleIcon, PaperPlaneIcon,
  CurrencyCircleDollarIcon, ArrowLeftIcon, DownloadSimpleIcon, FileArrowDownIcon,
} from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';

import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import CustomButton from '@/components/inputs/CustomButton';
import useLoading from '@/components/loading/hooks/useLoading';
import useSnackbar from '@/components/dialogs/snackbars/hooks/useSnackbar';
import useConfirm from '@/components/dialogs/hooks/useConfirm';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const statusVariants = {
  draft: 'outline',
  sent: 'default',
  paid: 'secondary',
  cancelled: 'destructive',
};

const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const InvoiceList = ({ companyId: companyIdProp, embedded = false }) => {
  const params = useParams();
  const companyId = companyIdProp || params.companyId;
  const [invoices, setInvoices] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { loading, Loading, setLoading } = useLoading(true);
  const { createSuccessSnackbar, createErrorSnackbar } = useSnackbar();
  const { openConfirm, ConfirmDialog } = useConfirm();

  const loadInvoices = async () => {
    const res = await Connections.postRequest(ApiEndpoints.invoicesAll, { companyId });
    if (res.ok) {
      setInvoices(res.data.invoices || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadInvoices().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleSend = async invoiceId => {
    const confirmed = await openConfirm(t('invoice.sendConfirm'));
    if (!confirmed) return;

    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.invoiceSend, { companyId, invoiceId });
    if (res.ok) {
      createSuccessSnackbar(t('invoice.sent'));
      await loadInvoices();
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const handleMarkPaid = async invoiceId => {
    setLoading(true);
    const res = await Connections.postRequest(ApiEndpoints.invoiceMarkPaid, { companyId, invoiceId });
    if (res.ok) {
      createSuccessSnackbar(t('invoice.markedPaid'));
      await loadInvoices();
    } else {
      createErrorSnackbar(res.errorMessage);
    }
    setLoading(false);
  };

  const handleDownload = async (invoice, variant) => {
    const res = await Connections.postBlobRequest(ApiEndpoints.invoiceDownload, {
      companyId, invoiceId: invoice._id, variant,
    });
    if (res.ok) {
      const base = invoice.invoiceNumberFormatted || invoice._id;
      triggerBlobDownload(res.blob, `${base}-${variant}.pdf`);
    } else {
      createErrorSnackbar(res.errorMessage);
    }
  };

  const formatDate = dateStr => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) return <Loading />;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{t('app.title')} | {t('invoices')}</title>
        </Helmet>
      )}
      <ConfirmDialog />
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2">
          {!embedded && (
            <button type="button" className={iconBtn} onClick={() => navigate('/companies')}>
              <ArrowLeftIcon size={20} />
            </button>
          )}
          <h1 className="text-2xl font-semibold">{t('invoices')}</h1>
        </div>
        <CustomButton onClick={() => navigate(`/companies/${companyId}/invoices/new`)}>
          <PlusIcon size={18} />
          {t('invoice.create')}
        </CustomButton>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t('invoice.number')}</th>
              <th className="px-4 py-3 font-medium">{t('customer')}</th>
              <th className="px-4 py-3 font-medium">{t('date')}</th>
              <th className="px-4 py-3 font-medium">{t('dueDate')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('total')}</th>
              <th className="px-4 py-3 font-medium">{t('status')}</th>
              <th className="px-4 py-3 font-medium text-right">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map(invoice => (
              <tr key={invoice._id} className="border-t hover:bg-muted/30">
                <td className="px-4 py-3 font-semibold">{invoice.invoiceNumberFormatted}</td>
                <td className="px-4 py-3">{invoice.customerSnapshot?.name || '-'}</td>
                <td className="px-4 py-3">{formatDate(invoice.issueDate)}</td>
                <td className="px-4 py-3">{formatDate(invoice.dueDate)}</td>
                <td className="px-4 py-3 text-right">
                  {invoice.totalGross?.toFixed(2)} {invoice.currency}
                </td>
                <td className="px-4 py-3">
                  <Badge
                    variant={statusVariants[invoice.status] || 'outline'}
                    style={{ borderRadius: '0.375rem' }}
                  >
                    {t(`invoice.status.${invoice.status}`)}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      className={iconBtn}
                      onClick={() => navigate(`/companies/${companyId}/invoices/${invoice._id}/edit`)}
                      title={t('edit')}
                    >
                      <PencilSimpleIcon size={18} />
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      onClick={() => handleDownload(invoice, 'original')}
                      title={t('invoice.downloadOriginal')}
                    >
                      <FileArrowDownIcon size={18} />
                    </button>
                    <button
                      type="button"
                      className={iconBtn}
                      onClick={() => handleDownload(invoice, 'copy')}
                      title={t('invoice.downloadCopy')}
                    >
                      <DownloadSimpleIcon size={18} />
                    </button>
                    <button
                      type="button"
                      className={`${iconBtn} hover:text-primary`}
                      onClick={() => handleSend(invoice._id)}
                      title={t('invoice.send')}
                    >
                      <PaperPlaneIcon size={18} />
                    </button>
                    {invoice.status === 'sent' && (
                      <button
                        type="button"
                        className={`${iconBtn} hover:text-secondary`}
                        onClick={() => handleMarkPaid(invoice._id)}
                        title={t('invoice.markPaid')}
                      >
                        <CurrencyCircleDollarIcon size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  {t('invoice.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
};

export default InvoiceList;
